import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import { useMountedState } from 'react-use';

import DashboardRoutes from '../Dashboard/routes';

import { deviceType } from '../../config';

import { validateData } from '../../utils/joi';
import { generateDynamicPath } from '../../utils/common';

import { IAppReduxState } from '../../redux/reducer';
import { authUpdateAction } from '../../redux/auth/auth.actions';

import ProductLogo from '../../components/Common/ProductLogo';
import { ButtonComp, TextInputComp } from '../../components/Forms';

import AccountsRoutes from '../Accounts/routes';

import * as constants from './constants';
import * as interfaces from './interfaces';
import * as utils from './utils';
import * as services from './services';
import AuthRoutes from './routes';

const DefaultFormError = {
  firstName: undefined,
  lastName: undefined,
  email: undefined,
  password: undefined,
  passwordConfirm: undefined,
  companyName: undefined,
};

function Register() {
  const isMounted = useMountedState();

  const history: any = useHistory();
  const {
    auth: {
      auth: { token: authToken },
      product,
    },
  } = useSelector((state: IAppReduxState) => state);
  const reduxActionDispatch = useDispatch();

  const [isFetching, setIsFetching] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(history.location?.state?.email || '');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  const [companyName, setCompanyName] = useState('');

  const [errors, setErrors] = useState(DefaultFormError);

  const formSubmit = async (event: React.ChangeEvent<any>) => {
    event.preventDefault();

    const data: interfaces.IRegisterReqData = {
      productKind: product.productKind,
      deviceType,
      firstName,
      lastName,
      email,
      password,
      companyName,
    };

    const validate = validateData(
      { ...data, passwordConfirm },
      constants.RegisterJoiScheme
    );

    if (validate.errors) {
      setErrors(validate.errors);
      return;
    }

    setIsFetching(true);
    setErrors(DefaultFormError);

    try {
      const result = await services.registerService(data);

      result.data.user = utils.formatAuthUser(result.data.user);

      //  Redux Auth Update
      reduxActionDispatch(authUpdateAction(result.data));

      const accountEditPath = generateDynamicPath(
        AccountsRoutes.routes.editAccount.path,
        { accountIndex: '0' }
      );

      history.push(accountEditPath, {
        successMsg: result.message,
      });

      return;
    } catch (error: any) {
      console.error(error);
    }
    if (!isMounted()) {
      return;
    }

    setIsFetching(false);
  };

  useEffect(() => {
    document.body.className = 'p-0 m-0';
    return () => {
      document.body.className = '';
    };
  });

  useEffect(() => {
    if (authToken) {
      history.push(DashboardRoutes.routes.home.path);
    }
  }, []);

  return (
    <div id="wrapper">
      <div className="session-wrap login-wrap d-flex align-items-center justify-content-center justify-content-lg-end">
        <div className="session-content contact-form-wrap text-center card shadow-box">
          <ProductLogo />
          <div>
            <form method="post" onSubmit={formSubmit}>
              <div className="row">
                <div className="col-6 col-md-6 col-lg-6">
                  <div className="form-group text-left mb-3">
                    <TextInputComp
                      name="firstName"
                      onChange={setFirstName}
                      label="First Name"
                      autoFocus
                      placeholder="First Name *"
                      value={firstName}
                      errorMsg={errors.firstName}
                    />
                  </div>
                </div>

                <div className="col-6 col-md-6 col-lg-6">
                  <div className="form-group text-left mb-3">
                    <TextInputComp
                      name="lastName"
                      onChange={setLastName}
                      label="Last Name"
                      placeholder="Last Name"
                      value={lastName}
                      errorMsg={errors.lastName}
                    />
                  </div>
                </div>

                <div className="col-12 col-md-12 col-lg-12">
                  <div className="form-group text-left mb-3">
                    <TextInputComp
                      name="email"
                      onChange={setEmail}
                      label="Email"
                      placeholder="Email *"
                      value={email}
                      errorMsg={errors.email}
                    />
                  </div>
                </div>

                <div className="col-12 col-md-12 col-lg-12">
                  <div className="form-group text-left mb-3">
                    <TextInputComp
                      name="companyName"
                      onChange={setCompanyName}
                      label="Company Name"
                      placeholder="Company Name"
                      value={companyName}
                      errorMsg={errors.companyName}
                    />
                  </div>
                </div>

                <div className="col-12 col-md-12 col-lg-12">
                  <div className="form-group text-left mb-3">
                    <TextInputComp
                      type="password"
                      name="password"
                      onChange={setPassword}
                      label="Password"
                      placeholder="Password *"
                      value={password}
                      errorMsg={errors.password}
                    />
                  </div>
                </div>

                <div className="col-12 col-md-12 col-lg-12">
                  <div className="form-group text-left mb-3">
                    <TextInputComp
                      type="password"
                      name="passwordConfirm"
                      onChange={setPasswordConfirm}
                      label="Confirm Password"
                      placeholder="Confirm Password *"
                      value={passwordConfirm}
                      errorMsg={errors.passwordConfirm}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <ButtonComp
                  loading={isFetching}
                  type="submit"
                  className="btn btn-one btn-block btn-primary"
                  text="Register"
                />
              </div>

              <div className="link-register">
                <span>
                  Already have an account?{' '}
                  <Link to={AuthRoutes.login.path}>Log In</Link>
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
